import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@promanage/ui';
import { ARREARS_STAGES, ARREARS_STAGE_LABELS, type ArrearsStage } from '@promanage/core';
import { TopBar } from '../components/TopBar';
import { TenanciesNav } from '../features/arrears/TenanciesNav';
import { ArrearsRegister } from '../features/arrears/ArrearsRegister';
import { StageLadder } from '../features/arrears/StageLadder';
import { trpc } from '../lib/trpc';
import { WorkspaceMenu, useWorkspace } from '../features/workspaces/WorkspaceMenu';
import { workspaceChoice } from '../features/workspaces/state';

export const Route = createFileRoute('/tenancies/arrears')({
  component: ArrearsPage,
});

type Filter = 'work' | 'all' | 'everyone' | ArrearsStage;

/**
 * Arrears — ROADMAP.md M2, the screen the rest of the chase hangs off.
 *
 * UNDER `/tenancies`, reached from the button on the tenancy list rather than from the rail.
 * Arrears is a view OF the tenancy book rather than a section beside it: every row is a letting,
 * every action lands on one, and the drill-down from here goes straight back to the tenancy
 * record. The rail carries the places a person navigates TO; an entry for a filtered view of one
 * of them is how a rail becomes a menu.
 *
 * A SIBLING OF THE LIST, not a panel inside it. It is still a screen somebody lives in — opened
 * first thing, worked down, closed — and a tab on the tenancy list would make the day's work
 * something you get to by first loading a list of every letting in the book.
 *
 * THE DEFAULT FILTER IS THE WORK, not the book. "Everyone behind" is the figure an agency already
 * knows; "who is waiting on me" is the one that decides the morning. Both are one click apart, and
 * the ladder above the list always describes the whole book so the two can be compared.
 */
function ArrearsPage() {
  const [filter, setFilter] = useState<Filter>('work');
  const workspace = useWorkspace({
    route: '/tenancies/arrears',
    state: { filters: { filter }, columns: [], sort: null },
    apply(state) {
      setFilter(
        workspaceChoice(state, 'filter', ['work', 'all', 'escalate', 'everyone'] as const, 'work'),
      );
    },
  });

  const register = useQuery(
    trpc.arrears.register.queryOptions(
      {
        ...(filter === 'work' ? { outstandingOnly: true } : {}),
        ...(filter === 'everyone' ? { includeCurrent: true } : {}),
        ...(isStage(filter) ? { stage: filter } : {}),
      },
      // Short, like the review queue's. Two people work an arrears list at month end, and a notice
      // one of them has just recorded must stop being asked for by the other quickly.
      { staleTime: 5_000 },
    ),
  );

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Arrears"
        subtitle="Rent that has not arrived, and what has been done about it"
        actions={<WorkspaceMenu workspace={workspace} />}
      />
      <TenanciesNav />

      <div className="flex flex-col gap-4">
        {register.data ? <StageLadder register={register.data} /> : null}
        <ArrearsRegister
          register={register.data}
          isPending={register.isPending}
          filters={
            <>
              <FilterButton current={filter} value="work" onPick={setFilter}>
                Waiting on us
              </FilterButton>
              <FilterButton current={filter} value="all" onPick={setFilter}>
                All behind
              </FilterButton>
              <FilterButton current={filter} value="escalate" onPick={setFilter}>
                {ARREARS_STAGE_LABELS.escalate}
              </FilterButton>
              <FilterButton current={filter} value="everyone" onPick={setFilter}>
                {/* How somebody checks that a tenancy they chased last week has actually
                    cleared, which is otherwise indistinguishable from it falling out of the
                    query. */}
                Every tenancy
              </FilterButton>
            </>
          }
        />
      </div>
    </div>
  );
}

function isStage(filter: Filter): filter is ArrearsStage {
  return (ARREARS_STAGES as readonly string[]).includes(filter);
}

function FilterButton({
  current,
  value,
  onPick,
  children,
}: {
  current: Filter;
  value: Filter;
  onPick: (filter: Filter) => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      size="sm"
      variant={current === value ? 'primary' : 'secondary'}
      onClick={() => onPick(value)}
    >
      {children}
    </Button>
  );
}
